import React, { View } from 'react-native';
import { Formik } from 'formik';
import {
  FormControl, NativeBaseProvider,
} from 'native-base';
import {
  Button, Dialog, Portal, Text,
} from 'react-native-paper';
import { useContext, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import Picker from '../../components/commons/Picker';
import { styles } from '../../screens/Common.style';
import { ModalContext } from '../../context/ModalContext';
import { CreateAchievement } from '../../gql/achievement/mutation';
import { FindOneEvent } from '../../gql/event/query';
import { FindOneUserQuery } from '../../gql/user/query';
import { computeWeightBasedOnCategorie, formatDate } from '../../utils/utils';
import { AGE_CATEGORY, EVENT_PARTICIPANT_MODE } from '../../utils/constant';

function Achievement() {
  const {
    displayCreateAchievementModal,
    toggleCreateAchievementModal,
  } = useContext(ModalContext);
  const [createAchievement] = useMutation(CreateAchievement);

  // Track which picker is currently open
  const [openPicker, setOpenPicker] = useState(null);

  const positions = [
    { label: 'Premier', value: 1 },
    { label: 'Deuxième', value: 2 },
    { label: 'Troisième', value: 3 },
    { label: 'Cinquième', value: 5 },
  ];

  // Filter events that have ended (endAt < now)
  const availableEvents = useMemo(() => {
    if (!displayCreateAchievementModal?.events) return [];
    const now = new Date();
    return displayCreateAchievementModal.events.filter(
      (e) => e.event.endAt && new Date(e.event.endAt) < now,
    );
  }, [displayCreateAchievementModal?.events]);

  const computeWeightCategories = (category, gender) => {
    const objCategorie = Object.values(AGE_CATEGORY)
      .find((c) => c.name === category);
    const weights = objCategorie.weights[gender.toLowerCase()];
    weights.push(weights[weights.length - 1]);
    return weights.map((e, i) => (i !== weights.length - 1 ? `-${e}` : `+${e}`));
  };

  // Determine if we need to show event picker (when eventId is not provided but events are)
  const showEventPicker = !displayCreateAchievementModal?.eventId
    && availableEvents.length > 0;

  // Get categories for the selected event
  const getCategoriesForEvent = (eventId) => {
    if (displayCreateAchievementModal?.categories) {
      return displayCreateAchievementModal.categories;
    }
    const eventData = availableEvents.find((e) => e.event.id === eventId);
    return eventData?.event?.categories || [];
  };

  const getInitialCategories = () => {
    if (displayCreateAchievementModal?.categories) {
      return displayCreateAchievementModal.categories;
    }
    if (showEventPicker && availableEvents.length > 0) {
      return availableEvents[0]?.event?.categories || [];
    }
    return [];
  };

  const initialCategories = getInitialCategories();

  // Z-index values - higher values for pickers that appear first
  const zIndexEvent = 6000;
  const zIndexRank = 5000;
  const zIndexCategory = 4000;
  const zIndexWeight = 3000;
  const zIndexMode = 2000;

  return displayCreateAchievementModal?.display ? (
    <Formik
      initialValues={{
        eventId: displayCreateAchievementModal?.eventId
          || (showEventPicker && availableEvents.length > 0
            ? availableEvents[0].event.id
            : null),
        rank: 1,
        category: initialCategories[0] || '',
        weightCategory: initialCategories[0]
          ? computeWeightBasedOnCategorie(
            displayCreateAchievementModal?.weight,
            displayCreateAchievementModal?.gender,
            initialCategories[0],
          )
          : '',
        mode: Object.keys(EVENT_PARTICIPANT_MODE)[0],
      }}
      validateOnChange
      onSubmit={async (values) => {
        await createAchievement({
          variables: {
            eventId: values.eventId || displayCreateAchievementModal.eventId,
            rank: Number(values.rank),
            category: values.category,
            weightCategory: values.weightCategory,
            userId: displayCreateAchievementModal.userId,
            mode: values.mode,
          },
          refetchQueries: [
            FindOneEvent,
            'findOneEvent',
            FindOneUserQuery,
            'findOneUser',
          ],
        });
        toggleCreateAchievementModal();
      }}
    >
      {({
        handleSubmit, values, errors, isValid, setFieldValue,
      }) => {
        const currentCategories = showEventPicker
          ? getCategoriesForEvent(values.eventId)
          : displayCreateAchievementModal.categories;

        return (
          <Dialog
            visible={displayCreateAchievementModal}
            dismissable
            onDismiss={toggleCreateAchievementModal}
          >
            <Dialog.Title>Enregistrer un résultat</Dialog.Title>
            <Dialog.Content>
              {showEventPicker && (
                <View style={{ zIndex: zIndexEvent }}>
                  <FormControl style={styles.field}>
                    <FormControl.Label>Compétition</FormControl.Label>
                    <Picker
                      selectedValue={values.eventId}
                      open={openPicker === 'event'}
                      setOpen={(open) => setOpenPicker(open ? 'event' : null)}
                      zIndex={zIndexEvent}
                      zIndexInverse={zIndexMode}
                      onValueChange={(value) => {
                        setFieldValue('eventId', value);
                        const categories = getCategoriesForEvent(value);
                        if (categories.length > 0) {
                          setFieldValue('category', categories[0]);
                          setFieldValue(
                            'weightCategory',
                            computeWeightBasedOnCategorie(
                              displayCreateAchievementModal?.weight,
                              displayCreateAchievementModal?.gender,
                              categories[0],
                            ),
                          );
                        }
                      }}
                    >
                      {availableEvents.map((e) => (
                        <Picker.Item
                          key={e.event.id}
                          label={`${e.event.name} - ${formatDate(e.event.startAt)}`}
                          value={e.event.id}
                        />
                      ))}
                    </Picker>
                  </FormControl>
                </View>
              )}
              <View style={{ zIndex: zIndexRank }}>
                <FormControl style={styles.field}>
                  <FormControl.Label>Rang</FormControl.Label>
                  <Picker
                    onValueChange={(value) => setFieldValue('rank', value)}
                    selectedValue={values.rank}
                    open={openPicker === 'rank'}
                    setOpen={(open) => setOpenPicker(open ? 'rank' : null)}
                    zIndex={zIndexRank}
                    zIndexInverse={zIndexWeight}
                  >
                    {positions
                      .map((k) => <Picker.Item label={k.label} value={k.value} key={k.value} />)}
                  </Picker>
                </FormControl>
              </View>
              {!!errors.rank && <Text style={styles.error}>{errors.rank}</Text>}
              <View style={{ zIndex: zIndexCategory }}>
                <FormControl style={styles.field}>
                  <FormControl.Label>Catégorie</FormControl.Label>
                  <Picker
                    selectedValue={values.category}
                    onValueChange={(value) => setFieldValue('category', value)}
                    open={openPicker === 'category'}
                    setOpen={(open) => setOpenPicker(open ? 'category' : null)}
                    zIndex={zIndexCategory}
                    zIndexInverse={zIndexRank}
                  >
                    {(currentCategories || [])
                      .map((k) => <Picker.Item label={k} value={k} key={k} />)}
                  </Picker>
                </FormControl>
              </View>
              <View style={{ zIndex: zIndexWeight }}>
                <FormControl style={styles.field}>
                  <FormControl.Label>Catégorie de poids</FormControl.Label>
                  <Picker
                    selectedValue={values.weightCategory}
                    onValueChange={(value) => setFieldValue('weightCategory', value)}
                    open={openPicker === 'weight'}
                    setOpen={(open) => setOpenPicker(open ? 'weight' : null)}
                    zIndex={zIndexWeight}
                    zIndexInverse={zIndexCategory}
                  >
                    {computeWeightCategories(values.category, displayCreateAchievementModal.gender)
                      .map((k) => <Picker.Item label={k} value={k} key={k} />)}
                  </Picker>
                </FormControl>
              </View>
              <View style={{ zIndex: zIndexMode }}>
                <FormControl style={styles.field}>
                  <FormControl.Label>Mode</FormControl.Label>
                  <Picker
                    selectedValue={values.mode}
                    onValueChange={(value) => setFieldValue('mode', value)}
                    open={openPicker === 'mode'}
                    setOpen={(open) => setOpenPicker(open ? 'mode' : null)}
                    zIndex={zIndexMode}
                    zIndexInverse={zIndexEvent}
                  >
                    {Object.entries(EVENT_PARTICIPANT_MODE)
                      .map((k) => <Picker.Item label={k[1]} value={k[0]} key={k[0]} />)}
                  </Picker>
                </FormControl>
              </View>
            </Dialog.Content>
            <Dialog.Actions>
              <Button onPress={toggleCreateAchievementModal}>
                Annuler
              </Button>
              <Button disabled={!isValid} onPress={handleSubmit}>
                Envoyer
              </Button>
            </Dialog.Actions>
          </Dialog>
        );
      }}
    </Formik>
  ) : null;
}

export default function CreateAchievementModal() {
  return (
    <Portal>
      <NativeBaseProvider>
        <Achievement />
      </NativeBaseProvider>
    </Portal>
  );
}
